import { NominaProcessStatus } from "src/utils/enum";

const ACTIVE_IN_PROCESS_STATUSES = [
  NominaProcessStatus.IN_CREATED_NOMINA,
  NominaProcessStatus.IN_INFORMED_NOMINA,
  NominaProcessStatus.IN_BANK_NOMINA,
];

const normalizeAmount = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0 ? amount : 0;
};

/**
 * Read the matched (bank-conciliated) amount of a selection/fraction.
 */
const readMatchedAmount = (selection) => {
  return normalizeAmount(selection?.matchedAmount ?? 0);
};

const getSelections = (nominaStatus) => {
  const rawSelections = nominaStatus?.inNominaProcess;
  if (Array.isArray(rawSelections)) return rawSelections;
  if (rawSelections && typeof rawSelections === "object") {
    return Object.values(rawSelections);
  }
  return [];
};

export const getPendingNominaAmount = (selection) => {
  const amount = normalizeAmount(selection?.amount);
  const matched = Math.min(amount, readMatchedAmount(selection));

  return Math.max(0, amount - matched);
};

export const getNominaApprovedPendingAmount = (nominaStatus) => {
  const selections = getSelections(nominaStatus);

  return selections.reduce((total, selection) => {
    if (selection?.status !== NominaProcessStatus.APPROVED) return total;
    return total + getPendingNominaAmount(selection);
  }, 0);
};

export const getNominaInProcessPendingAmount = (nominaStatus) => {
  const selections = getSelections(nominaStatus);

  return selections.reduce((total, selection) => {
    if (!ACTIVE_IN_PROCESS_STATUSES.includes(selection?.status)) return total;
    return total + getPendingNominaAmount(selection);
  }, 0);
};

export const getNominaSelectedAmount = (nominaStatus) => {
  const selections = getSelections(nominaStatus);

  return selections.reduce((total, selection) => {
    if (selection?.status !== NominaProcessStatus.SELECTED) return total;
    return total + normalizeAmount(selection?.amount);
  }, 0);
};

export const getReservedNominaAmount = (nominaStatus) => {
  return (
    getNominaSelectedAmount(nominaStatus) +
    getNominaInProcessPendingAmount(nominaStatus) +
    getNominaApprovedPendingAmount(nominaStatus)
  );
};

export const getDocumentAvailablePayableAmount = (documentData) => {
  const remaining = normalizeAmount(documentData?.remaining);
  const reservedNominaAmount = getReservedNominaAmount(documentData?.nominaStatus);

  return Math.max(0, remaining - reservedNominaAmount);
};

export const getDocumentSelectableAmount = (documentData) => {
  return getDocumentAvailablePayableAmount(documentData);
};

export const getDocumentSelectableEditMaxAmount = (documentData) => {
  return (
    getDocumentSelectableAmount(documentData) +
    normalizeAmount(documentData?.userSelected)
  );
};

export const getDocumentsAvailablePayableTotal = (documents = []) => {
  return documents.reduce(
    (total, document) => total + getDocumentAvailablePayableAmount(document),
    0,
  );
};

export const getDocumentsDelayedAvailablePayableTotal = (documents = []) => {
  return documents.reduce((total, document) => {
    if ((document?.daysToExpire || 0) >= 0) return total;
    return total + getDocumentAvailablePayableAmount(document);
  }, 0);
};

/** Hay monto en estado SELECTED (chip "Seleccionada"). */
export const hasDocumentNominaSelection = (documentData) => {
  return (
    getNominaSelectedAmount(documentData?.nominaStatus) > 0 ||
    normalizeAmount(documentData?.userSelected) > 0
  );
};

/** El usuario actual tiene monto SELECTED en este documento. */
export const hasCurrentUserNominaSelection = (documentData) => {
  return normalizeAmount(documentData?.userSelected) > 0;
};

/**
 * Checkbox marcado solo si el usuario actual seleccionó el documento para nómina
 * y no queda monto por seleccionar. No se confía en isChecked huérfano del API
 * (queda true tras deseleccionar si el merge conserva el valor anterior).
 */
export const isDocumentFullySelectedForNomina = (documentData) => {
  if (!documentData) return false;
  if (!hasCurrentUserNominaSelection(documentData)) return false;

  const amountToSelect = normalizeAmount(documentData?.toSelect);
  if (amountToSelect === 0) return true;

  const selectedAmount = Math.max(
    getNominaSelectedAmount(documentData?.nominaStatus),
    normalizeAmount(documentData?.userSelected),
  );
  const selectableTotal =
    getDocumentSelectableAmount(documentData) + selectedAmount;

  return selectableTotal > 0 && selectedAmount >= selectableTotal;
};

export const isDocumentPartiallySelectedForNomina = (documentData) => {
  return (
    hasCurrentUserNominaSelection(documentData) &&
    !isDocumentFullySelectedForNomina(documentData)
  );
};

export const getProviderDocumentsCheckState = (documents = []) => {
  const enabled = documents.filter((document) => !document?.isCheckDisabled);
  if (enabled.length === 0) {
    return { isChecked: false, isPartial: false };
  }

  const fullySelectedCount = enabled.filter(isDocumentFullySelectedForNomina).length;
  const isChecked = fullySelectedCount === enabled.length;
  const isPartial =
    !isChecked &&
    enabled.some(
      (document) =>
        isDocumentFullySelectedForNomina(document) ||
        isDocumentPartiallySelectedForNomina(document),
    );

  return { isChecked, isPartial };
};

export const withDerivedNominaDocumentCheck = (documentData) => ({
  ...documentData,
  isChecked: isDocumentFullySelectedForNomina(documentData),
});

export const withDerivedNominaProviderCheck = (provider) => {
  const documents = (provider?.documents || []).map(withDerivedNominaDocumentCheck);
  const { isChecked } = getProviderDocumentsCheckState(documents);

  return {
    ...provider,
    documents,
    isChecked,
  };
};
